import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RESET_ORDERS_PAGINATION } from 'redux/actions/types';
import { isPermissionWithCompanyType } from 'hooks/useCompanyType';
import { isBrand } from 'hooks/useBrandType';
import {
  isPosProviderConnected,
  isTableOrderingProvider,
} from 'utils/checkAccessDenied';
import usePermission from './permission';
import usePermissionPlan from './permissionPlan';
import * as all from 'constants/permissions';

// Material UI Icons
import HomeIcon from '@mui/icons-material/Home';
import ListAltIcon from '@mui/icons-material/ListAlt';
import HistoryIcon from '@mui/icons-material/History';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import LanguageIcon from '@mui/icons-material/Language';
import InventoryIcon from 'assets/icons/inventory.png';
import { BUSINESS_TYPE_CATEGORY } from 'constants/companyTypes';

const useSidebarPages = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const checkPermission = usePermission();
  const checkPermissionPlan = usePermissionPlan();
  const providers = useSelector(({ providers }) => providers);
  const { newOrders } = useSelector(({ orders }) => orders);
  const { isTypeSingle } = isPermissionWithCompanyType();
  const { isTypeBrand } = isBrand();

  const getAvailableSidebarPages = useCallback(() => {
    const { currentBusiness } = useSelector(({ business }) => business);
    const isTableOrdering = isTableOrderingProvider(providers?.items);
    const isPosConnected = isPosProviderConnected(providers?.items);
    const isInventoryStore = currentBusiness?.businessTypes?.includes(
      BUSINESS_TYPE_CATEGORY.inventoryStore
    );
    const availablePages = [];

    // Home page
    if (isTypeSingle && checkPermission(all['HOME_REPORT_FILTER'])) {
      availablePages.push({
        id: 'home',
        label: t('Home'),
        path: '/',
        icon: HomeIcon,
        permission: 'HOME_REPORT_FILTER',
        isSelected: location.pathname === '/',
      });
    }

    // Orders page
    if (!isTypeBrand && checkPermission(all['ORDER_VIEW'])) {
      availablePages.push({
        id: 'orders',
        label: t('Orders'),
        path: '/orders',
        icon: ListAltIcon,
        permission: 'ORDER_VIEW',
        isSelected: location.pathname.includes('orders'),
        badge: newOrders,
        onClick: () => dispatch({ type: RESET_ORDERS_PAGINATION }),
      });
    }

    // Chargeback page
    if (
      (checkPermission(all['SUPER']) ||
        !checkPermissionPlan(all['EXTERNAL_BUNDLE'])) &&
      checkPermission(all['SETTINGS_USERS'])
    ) {
      availablePages.push({
        id: 'chargeback',
        label: t('Chargeback'),
        path: '/chargeback/new',
        icon: MoneyOffIcon,
        permission: 'SETTINGS_USERS',
        isSelected: location.pathname.includes('chargeback'),
        onClick: () => dispatch({ type: RESET_ORDERS_PAGINATION }),
      });
    }

    // Order History page
    if (!isTypeBrand && checkPermission(all['ORDER_VIEW'])) {
      availablePages.push({
        id: 'history',
        label: t('Order history'),
        path: '/history',
        icon: HistoryIcon,
        permission: 'ORDER_VIEW',
        isSelected: location.pathname.includes('history'),
        badge: newOrders,
        onClick: () => dispatch({ type: RESET_ORDERS_PAGINATION }),
      });
    }

    // Menu page
    if (checkPermission(all['MENU_VIEW'])) {
      availablePages.push({
        id: 'menu',
        label: t('menu.name'),
        path: '/menu/overview',
        icon: RestaurantMenuIcon,
        permission: 'MENU_VIEW',
        isSelected: location.pathname.includes('/menu'),
      });
    }

    // Table Ordering page
    if (isTableOrdering && checkPermission(all['SETTINGS_USERS'])) {
      availablePages.push({
        id: 'table-ordering',
        label: 'Table Ordering',
        path: '/table-ordering/tables',
        icon: TableRestaurantIcon,
        permission: 'SETTINGS_USERS',
        isSelected: location.pathname.includes('/table-ordering'),
      });
    }

    // Table Setup page
    if (isPosConnected && checkPermission(all['SETTINGS_USERS'])) {
      availablePages.push({
        id: 'table-setup',
        label: 'Table Setup',
        path: '/table-setup/tables',
        icon: TableRestaurantIcon,
        permission: 'SETTINGS_USERS',
        isSelected: location.pathname.includes('/table-setup'),
      });
    }

    // Reports page
    if (!isTypeBrand && checkPermission(all['HOME_REPORT_FILTER'])) {
      availablePages.push({
        id: 'reports',
        label: t('sidebar.reports'),
        path: '/reports',
        icon: AssessmentIcon,
        permission: 'HOME_REPORT_FILTER',
        isSelected: location.pathname.includes('/reports'),
      });
    }

    // Providers page
    if (
      isTypeSingle &&
      checkPermissionPlan(all['APP_BUNDLE']) &&
      checkPermission(all['PROVIDERS_VIEW'])
    ) {
      availablePages.push({
        id: 'providers',
        label: t('sidebar.providers'),
        path: '/providers',
        icon: SettingsIcon,
        permission: 'PROVIDERS_VIEW',
        planPermission: 'APP_BUNDLE',
        isSelected: location.pathname.includes('/providers'),
      });
    }

    // Website page
    if (
      (checkPermission(all['SUPER']) ||
        !checkPermissionPlan(all['EXTERNAL_BUNDLE'])) &&
      !isTypeBrand &&
      checkPermissionPlan(all['WEB_BUNDLE']) &&
      checkPermission(all['SETTINGS_WEBSITE'])
    ) {
      availablePages.push({
        id: 'website',
        label: t('menu.website'),
        path: '/website/main',
        icon: LanguageIcon,
        permission: 'SETTINGS_WEBSITE',
        planPermission: 'WEB_BUNDLE',
        isSelected: location.pathname.includes('/website'),
      });
    }

    // Settings page
    if (checkPermission(all['ORDER_VIEW'])) {
      availablePages.push({
        id: 'settings',
        label: t('sidebar.settings'),
        path: '/settings/business',
        icon: SettingsIcon,
        permission: 'ORDER_VIEW',
        isSelected:
          location.pathname.includes('/settings') &&
          !location.pathname.includes('table-ordering') &&
          !location.pathname.includes('/website') &&
          !location.pathname.includes('/providers'),
      });
    }

    // Help page
    if (checkPermission(all['HELP_VIEW'])) {
      availablePages.push({
        id: 'help',
        label: t('help.name'),
        path: '/help',
        icon: HelpOutlineIcon,
        permission: 'HELP_VIEW',
        isSelected: location.pathname.includes('/help'),
      });
    }

    // Schedule page
    if (checkPermission(all['SCHEDULE_READ'])) {
      availablePages.push({
        id: 'schedule',
        label: t('schedule.name'),
        path: '/schedule',
        icon: AccessTimeIcon,
        permission: 'SCHEDULE_READ',
        isSelected: location.pathname.includes('/schedule'),
      });
    }

    // Inventory page
    if (isInventoryStore && checkPermission(all['MENU_EDIT'])) {
      availablePages.push({
        id: 'inventory',
        label: 'Inventory',
        path: location.pathname,
        icon: InventoryIcon,
        permission: 'MENU_EDIT',
        isSelected: location.pathname.includes('/inventory'),
      });
    }

    return availablePages;
  }, [
    providers?.items,
    isTypeSingle,
    isTypeBrand,
    checkPermission,
    checkPermissionPlan,
    t,
    location.pathname,
    newOrders,
    all,
  ]);

  return {
    getAvailableSidebarPages,
    availableSidebarPages: getAvailableSidebarPages(),
  };
};

export default useSidebarPages;
